<template>
  <div v-if="data.captcha_object" :class="parentClasses">
    <div><b>Sicherheitsabfrage</b></div>
    <img :src="data.captcha_object.captcha_image_url" />
    <button
      v-if="hasAudioCaptcha"
      class="btn btn-primary captcha-button"
      type="button"
      title="Zeichenfolge aussprechen"
      v-on:click="playAudioCaptcha('audio-captcha')"
      aria-label="Zeichenfolge aussprechen. Zeichenfolge beinhaltet Kleinbuchstaben und Zahlen."
    >
      <font-awesome-icon icon="volume-up" class="fa fa-fw fa-lg" />
    </button>
    <button
      id="refresh-captcha-btn"
      class="btn btn-primary captcha-button"
      type="button"
      title="Neue Zeichenfolge anfordern."
      v-on:click="refetchCaptcha()"
      aria-label="Neue Zeichenfolge anfordern."
    >
      <font-awesome-icon icon="refresh" class="fa fa-fw fa-lg" />
    </button>
    <input
      type="text"
      id="captcha_answer"
      ref="captchaInput"
      :class="inputFieldClasses"
      name="captcha"
      v-model="value"
      required
    />
    <div class="text-danger" v-if="hasError">Bitte geben Sie die richtige Antwort für die Sicherheitsabfrage ein.</div>
    <div class="text-danger" v-if="serverError">
      {{ serverError }}
    </div>
  </div>
</template>

<script>
/**
 * Captcha form. It handles refetching and emits information on updated captcha data to parent (through 'captcha-updated')
 */
import { computed, reactive, onMounted, ref, defineExpose } from 'vue';
import { useField } from 'vee-validate';
import { RepositoryFactory } from '@/api/repositoryFactory';
import { domainStore } from '@/store/domain-store';

export default {
  name: 'Captcha',
  emits: ['captcha-updated'],
  props: {
    serverError: {
      type: String,
      required: false,
      default: '',
    },
  },

  setup(props, context) {
    const DomainRepository = RepositoryFactory.get('domain');
    const data = reactive({
      captcha_object: null, // captcha data (id, image uri, audio uri) will be stored here
    });
    const captchaInput = ref(null);
    const baseApi = process.env.VUE_APP_SERVER_BASE_URL + 'api/v1/domain/' + domainStore.getState().domainid;

    //+---------------------+
    //|     other methods   |
    //+---------------------+
    const playAudioCaptcha = function () {
      const a = new Audio(data.captcha_object.captcha_audio_url);
      a.play();
    };

    function validate(value) {
      // If no captcha, this component should not be visible anyway
      if (data.captcha_object == null) return true;

      // Simply check for empty answer
      const finalvalue = value;
      const returnValue = {
        value: finalvalue,
        valueHuman: finalvalue,
        validated: finalvalue != '',
        name: 'captcha',
        code: 'captcha',
        type: 'text',
        data: {
          captcha_id: data.captcha_object.captcha_id,
        },
      };
      context.emit('validate', returnValue);
      return returnValue.validated;
    }

    // Retrieve new captcha (this is also called when the component is mounted)
    function refetchCaptcha() {
      DomainRepository.postRefetchCaptcha()
        .then(response => {
          // overwrite captcha urls with api-endpoint defined in ENV to avoid CORS errors in external embedded widgets
          response.captcha_image_url = baseApi + '/captcha_image/' + response.captcha_id;
          if (response.captcha_audio_url && response.captcha_audio_url !== '') {
            // only set audio-url if it was originally set from server
            response.captcha_audio_url = baseApi + '/captcha_audio/' + response.captcha_id;
          }
          context.emit('captcha-updated', response);
          data.captcha_object = response;
        })
        .catch(err => {
          console.error(err);
        });
    }

    const focusCaptchaField = () => {
      if (captchaInput.value) {
        captchaInput.value.focus();
      }
    };

    //+---------------------+
    //|     Variables       |
    //+---------------------+
    const { errorMessage, value } = useField('captcha', validate, {
      initialValue: '',
    });

    //+---------------------+
    //|     computed()      |
    //+---------------------+
    const hasError = computed(() => {
      return errorMessage.value != undefined;
    });
    const hasAudioCaptcha = computed(() => {
      return (
        'captcha_audio_url' in data.captcha_object &&
        data.captcha_object.captcha_audio_url != '' &&
        data.captcha_object.captcha_audio_url
      );
    });
    const inputFieldClasses = computed(() => {
      return {
        'form-control': true,
        'is-invalid': hasError.value || props.serverError,
      };
    });
    const parentClasses = computed(() => {
      return {
        'form-group': true,
        'has-error': hasError.value || props.serverError,
      };
    });

    // Expose the child method so it's callable by parent
    context.expose({ focusCaptchaField });

    //+---------------------+
    //|     Lifecycle       |
    //+---------------------+
    onMounted(() => {
      refetchCaptcha();
    });

    return {
      data,
      errorMessage,
      value,
      hasError,
      hasAudioCaptcha,
      captchaInput,
      parentClasses,
      inputFieldClasses,
      playAudioCaptcha,
      refetchCaptcha,
    };
  },
};
</script>

<style scoped>
.captcha-button {
  padding: 5px !important;
  margin: 2px;
}
</style>
